/**
 * Server-backed commerce cart (FOOD + PHARMACY).
 *
 * Replaces the old client-side zustand cart. Two things move with it:
 *
 *  - **Money is no longer computed here.** Line totals, the delivery fee and the
 *    grand total all come from the server, so the number on the Pay button is by
 *    construction the number checkout will charge. The old store duplicated the
 *    free-delivery threshold and fee, which is a second source of truth for money.
 *  - **The cart survives.** It is stored against the account, so it outlives a
 *    refresh, a re-login, and a different device.
 *
 * Every mutation returns the whole re-priced cart, so the response is written
 * straight into the query cache — no refetch round-trip, and the UI can never
 * show a total the server didn't just calculate.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { use_session_store } from '@/lib/session-store';
import type { CommerceVertical } from './types';

/** Why a line can't be ordered. `REMOVED` = the product is gone from the catalog. */
export type CartLineUnavailableReason = 'REMOVED' | 'UNAVAILABLE';

export type CartLine = {
  /** Cart-line id — what the update/remove endpoints address, NOT the variant id. */
  id: string;
  variant_id: string;
  product_id: string;
  /** Opaque routing token: groups lines into per-vendor shipments, never shown to the shopper. */
  vendor_profile_id: string;
  vendor_business_name: string | null;
  vertical: CommerceVertical;
  name: string;
  variant_label: string | null;
  brand: string | null;
  pack_size: string | null;
  category: string | null;
  quantity: number;
  unit_price_inr: string;
  /** Struck-through price, when the vendor set one above the selling price. */
  mrp_inr: string | null;
  /** Derived from MRP vs price; null unless MRP exceeds price. */
  discount_pct: number | null;
  line_total_inr: string;
  /** Product images, same shape and paths as the Shop. Empty for a removed product. */
  images: import('@/features/catalog-image/types').CatalogImage[];
  is_available: boolean;
  is_prescription_required: boolean;
  unavailable_reason: CartLineUnavailableReason | null;
};

/** Expected delivery (client change, 9 Oct 2026). */
export type DeliveryEta = { min_minutes: number; max_minutes: number; label: string };

export type Cart = {
  items: CartLine[];
  shipments_preview: {
    vendor_profile_id: string;
    vendor_business_name: string | null;
    item_count: number;
    subtotal_inr: string;
    estimated_delivery: (DeliveryEta & { is_vendor_set: boolean }) | null;
  }[];
  item_count: number;
  goods_total_inr: string;
  delivery_fee_inr: string;
  grand_total_inr: string;
  /** When the whole order should arrive (the slowest vendor). Null for an empty cart. */
  estimated_delivery: DeliveryEta | null;
  /** Any line needs a prescription — gates the checkout upload. */
  prescription_required: boolean;
  /** Some line can't be ordered; it is excluded from the totals and blocks checkout. */
  has_unorderable_items: boolean;
};

const EMPTY_CART: Cart = {
  items: [],
  shipments_preview: [],
  item_count: 0,
  goods_total_inr: '0',
  delivery_fee_inr: '0',
  grand_total_inr: '0',
  estimated_delivery: null,
  prescription_required: false,
  has_unorderable_items: false,
};

export const CART_KEY = ['commerce-cart'] as const;

async function fetch_cart(): Promise<Cart> {
  const res = await api.get<Cart>('/user/cart');
  return res.data;
}

/**
 * Copy only — "Free delivery over ₹X". The fee itself is whatever the server says;
 * this never enters an arithmetic path.
 */
export const FREE_DELIVERY_THRESHOLD_INR = 499;

/**
 * Everything a cart UI needs, with the same shape the old client store exposed
 * (`qty_of`, `add`, `set_qty`, `inc`) so call sites read the same — except the
 * totals, which are now strings from the server rather than local arithmetic.
 */
export function use_cart() {
  const qc = useQueryClient();
  const token = use_session_store((s) => s.onboarding_token);

  const query = useQuery({
    queryKey: CART_KEY,
    queryFn: fetch_cart,
    // The Shop is browsable signed-out; the cart is not. Don't fire a guaranteed 401.
    enabled: !!token,
    staleTime: 30_000,
  });

  /** Server hands back the full cart on every write — trust it over any local guess. */
  const write = (fn: () => Promise<Cart>) =>
    fn().then((cart) => {
      qc.setQueryData(CART_KEY, cart);
      return cart;
    });

  const add_m = useMutation({
    mutationFn: (input: { variant_id: string; quantity?: number }) =>
      write(() => api.post<Cart>('/user/cart/items', input).then((r) => r.data)),
  });
  const update_m = useMutation({
    mutationFn: (input: { item_id: string; quantity: number }) =>
      write(() =>
        api.patch<Cart>(`/user/cart/items/${input.item_id}`, { quantity: input.quantity }).then((r) => r.data),
      ),
  });
  const remove_m = useMutation({
    mutationFn: (item_id: string) =>
      write(() => api.delete<Cart>(`/user/cart/items/${item_id}`).then((r) => r.data)),
  });
  const clear_m = useMutation({
    mutationFn: () => write(() => api.delete<Cart>('/user/cart').then((r) => r.data)),
  });

  const cart = query.data ?? EMPTY_CART;
  const line_of = (variant_id: string) => cart.items.find((i) => i.variant_id === variant_id);

  return {
    cart,
    is_loading: query.isLoading,
    /** True while any write is in flight — steppers disable so a double-tap can't race. */
    is_busy: add_m.isPending || update_m.isPending || remove_m.isPending || clear_m.isPending,
    error: add_m.error ?? update_m.error ?? remove_m.error ?? null,

    qty_of: (variant_id: string) => line_of(variant_id)?.quantity ?? 0,
    add: (variant_id: string, quantity = 1) => add_m.mutate({ variant_id, quantity }),
    inc: (variant_id: string) => {
      const line = line_of(variant_id);
      if (line) update_m.mutate({ item_id: line.id, quantity: line.quantity + 1 });
      else add_m.mutate({ variant_id, quantity: 1 });
    },
    set_qty: (variant_id: string, quantity: number) => {
      const line = line_of(variant_id);
      if (!line) return;
      if (quantity <= 0) remove_m.mutate(line.id);
      else update_m.mutate({ item_id: line.id, quantity });
    },
    remove: (variant_id: string) => {
      const line = line_of(variant_id);
      if (line) remove_m.mutate(line.id);
    },
    clear: () => clear_m.mutate(),
    /** After checkout the server has already emptied it — just re-read. */
    refresh: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  };
}
